import localforage from 'localforage';
import { localRead, localRemove } from '@/libs/util';
import Vue from 'vue';
import { desktopMajorVersion } from '@/libs/client';
import SessionDriver from './SessionDriver';

// 为 localForage 添加驱动
localforage.defineDriver(SessionDriver);

const env = process.env.VUE_APP_CURRENTMODE || 'dev';

const desktopVersion = desktopMajorVersion();

// 针对低版本桌面端，不使用indexDB存储方式
const driver = desktopVersion === 'v1' ? [localforage.LOCALSTORAGE] : [localforage.INDEXEDDB, localforage.LOCALSTORAGE];
// 针对低版本桌面端，不使用indexDB存储方式
const logDriver = desktopVersion === 'v1' ? [SessionDriver._driver] : [localforage.INDEXEDDB, SessionDriver._driver];

console.log(['driver-desktopVersion:', desktopVersion, driver]);

// 当不支持indexedDB时，会自动降级到localStorage
const globalConfig = {
    driver,
    name: `ss_cache_${env}`,
    version: 1.0,
};

// 本地缓存配置
const cache = localforage.createInstance({
    ...globalConfig,
    storeName: 'cache',
    description: '全局缓存储数据库',
});

// 日志缓存数据库，此数据库不进行降级处理，只使用indexedDB
const logCache = localforage.createInstance({
    ...globalConfig,
    driver: logDriver,
    storeName: 'cache_log',
    description: '日志缓存储数据库',
});

// 聊天缓存配置，此数据库不进行降级处理，只使用indexedDB
// const chatCache = localforage.createInstance({
//     ...globalConfig,
//     driver: localforage.INDEXEDDB,
//     name: `ss_chat_${env}`,
//     storeName: 'cache_chat',
//     description: '在线聊天缓存储数据库',
// });

const BrowserResults = {
    canDetect: true,
    fsMode: '' as string | boolean,
    storageMode: false,
    estimate: {},
};
// 判断浏览器是否处于隐私模式&存储空间大小
const judgeIncognitoMode = (callback: Function) => {
    try {
        const fs = (window as any).RequestFileSystem || (window as any).webkitRequestFileSystem;
        if (fs) {
        // Chrome, Edge, Opera
            fs((window as any).TEMPORARY, 100, () => {
                BrowserResults.fsMode = false;
            }, () => {
                BrowserResults.fsMode = true;
            });
        }
        if (navigator.storage && navigator.storage.estimate) {
        // Firefox, Safari（部分）
            navigator.storage.estimate().then((estimate) => {
                // console.log(['isIncognitoMode navigator', estimate]);
                // Firefox 无痕模式下 quota 会很小（大约 0）
                const quota = estimate.quota || 0;
                BrowserResults.estimate = estimate;
                if (quota < 120000000) {
                // 很小说明可能是隐私模式
                    BrowserResults.storageMode = true;
                } else {
                    BrowserResults.storageMode = false;
                }
                callback(BrowserResults);
            }).catch(() => {
                BrowserResults.storageMode = false;
                callback(BrowserResults);
            });
        } else {
        // 无法检测
            BrowserResults.canDetect = true;
            callback(BrowserResults);
        }
    } catch (error) {
        callback(BrowserResults);
    }
};

interface ConstructorParams {
    db: LocalForage;
    downgradeDriver?: string;
}

interface CacheData {
    data: any;
    expire: number;
    timestamp: number;
}

interface TagData {
    expire: number | undefined;
    key: string;
}

class CacheIntegrated {
    constructor({ db, downgradeDriver }: ConstructorParams) {
        this.db = db;
        this.downgradeDriver = downgradeDriver || '';
        this.clearOverdue();
        this.supportList = {
            indexDB: this.db.supports(localforage.INDEXEDDB),
            localStorage: this.db.supports(localforage.LOCALSTORAGE),
        };
        if (downgradeDriver) {
            judgeIncognitoMode((res: typeof BrowserResults) => {
                if (res && res.storageMode) {
                    console.log(['切换驱动', downgradeDriver, res]);
                    Vue.prototype.$log('LOCAL_FORAGE_ERROR', 'LOCAL_FORAGE_CHECK_DRIVER', { data: { driver: this.db.driver(), res } });
                    this.db.setDriver(downgradeDriver);
                }
            });
        }
    }

    // 缓存驱动支持的列表
    supportList: { [key in string]: boolean } = {};

    // 缓存集合key
    cacheExpire = 'SS-CACHE:TAG';

    db: LocalForage;

    downgradeDriver: string = '';

    /**
     * 获取当前时间戳
     */
    time(): number {
        return Math.round(Date.now() / 1000);
    }

    /**
     * 获取缓存数据
     * @param key 键
     * @param defaultValue 默认值
     * @returns
     */
    getItem(key: string, defaultValue: any = ''): Promise<any> {
        return new Promise((resolve, reject) => {
            this.db.getItem(key).then((val: CacheData | unknown) => {
                if (!val) {
                    // 获取不到数据时，查看本地缓存是否有对应的数据，用于从localstorage平滑迁移到localForage
                    let localData = localRead(key);
                    if (localData) {
                        // 有本地缓存数据时，将数据存入indexedDB，并返回
                        try {
                            // 尝试解析数据
                            localData = JSON.parse(localData);
                        } catch (_) {
                            //
                        }
                        // 将数据存入indexedDB
                        this.setItem(key, localData);
                        // 清理localStorage中的数据
                        localRemove(key);
                        // 返回数据
                        resolve(localData);
                    } else {
                        resolve(defaultValue);
                    }
                } else if (typeof val === 'object') {
                    const { data = defaultValue, expire } = val as CacheData;
                    if (expire > 0 && expire < this.time()) {
                        // 过期数据，删除
                        this.removeItem(key);
                        resolve(defaultValue);
                    } else {
                        resolve(data);
                    }
                } else {
                    resolve(defaultValue);
                }
            }).catch((err) => {
                console.log('key', key, 'value', err);
                // 获取不到数据时，默认返回空字符串
                resolve(defaultValue);
                let err_data = err;
                try {
                    err_data = JSON.stringify(err);
                } catch (_) {
                    //
                }
                Vue.prototype.$log('LOCAL_FORAGE_ERROR', 'LOCAL_FORAGE_GET_ITEM', { data: { key, err: err_data, supportList: this.supportList } });
            });
        });
    }

    /**
     * 提交错误日志
     * @param key 存储的key
     * @param err_1 错误1
     * @param err_2 错误2
     */
    private _postErrorMsgLog(key: string, err_1: any = {}, err_2: any = {}) {
        let err_data_1: any = {};
        let err_data_2: any = {};
        try {
            err_data_1 = {
                err: err_1,
                stringify: JSON.stringify(err_1),
                toString: err_1?.toString(),
                name: err_1?.name ?? 'not data',
                message: err_1?.message ?? 'not data',
                constructor_name: err_1?.constructor?.name ?? 'not data',
            };
        } catch (catch_err) {
            err_data_1 = { err: err_1, catch_err };
        }
        try {
            err_data_2 = {
                err: err_2,
                stringify: JSON.stringify(err_2),
                toString: err_2?.toString(),
                name: err_2?.name ?? 'not data',
                message: err_2?.message ?? 'not data',
                constructor_name: err_2?.constructor?.name ?? 'not data',
            };
        } catch (catch_err) {
            err_data_2 = { err: err_2, catch_err };
        }
        const err = {
            err_data_1,
            err_data_2,
        };
        console.log('LOCAL_FORAGE_ERROR', err);
        const postData: any = { key, err, supportList: this.supportList };
        if (this.downgradeDriver) {
            postData.browserConfig = BrowserResults;
        }
        Vue.prototype.$log('LOCAL_FORAGE_ERROR', 'LOCAL_FORAGE_SET_ITEM', { data: postData });
    }

    /**
     * 设置缓存数据
     * @param key 键
     * @param value 值
     * @param expire 过期时间，单位秒，默认0，表示永不过期
     * @returns
     */
    setItem(key: string, value: any, expire: number = 0): Promise<any> {
        return new Promise((resolve, reject) => {
            const timestamp = this.time();
            const _t = expire === 0 ? 0 : timestamp + expire;

            const data: CacheData = {
                data: value ?? '',
                expire: _t,
                timestamp,
            };
            this.db.setItem(key, data).then((val) => {
                resolve(val);
            }).catch((err_1) => {
                // 延迟重新设置缓存
                setTimeout(() => {
                    this.db.setItem(key, data).then((val_2) => {
                        resolve(val_2);
                    }).catch((err_2) => {
                        resolve('');
                        this._postErrorMsgLog(key, err_1, err_2);
                    });
                }, 10);
            });

            if (_t > 0) {
                // 只保存具有过期时间的缓存key
                this.db.getItem(this.cacheExpire).then((tag) => {
                    let newTag: TagData[] = [];
                    const newKeys: string[] = [];
                    if (Array.isArray(tag) && tag.length) {
                        newTag = tag.map((item: TagData) => {
                            newKeys.push(item.key);
                            if (item.key === key) {
                                item.expire = _t;
                            }
                            return item;
                        });
                    }
                    if (!newKeys.length || newKeys.indexOf(key) === -1) {
                        newTag.push({
                            key,
                            expire: _t,
                        });
                    }
                    this.db.setItem(this.cacheExpire, newTag);
                });
            }
        });
    }

    /**
     * 移除缓存数据
     * @param key 键
     * @returns
     */
    removeItem(key: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.db.removeItem(key).then((val) => {
                resolve(val);
            }).catch((err) => {
                reject(err);
                let err_data = err;
                try {
                    err_data = JSON.stringify(err);
                } catch (_) {
                    //
                }
                Vue.prototype.$log('LOCAL_FORAGE_ERROR', 'LOCAL_FORAGE_REMOVE_ITEM', { data: { key, err: err_data, supportList: this.supportList } });
            });
        });
    }

    /**
     * 清除过期缓存
     */
    async clearOverdue() {
        const cacheValue: TagData[] = await this.db.getItem(this.cacheExpire) || [];
        const time = this.time();
        const newTagValue: any[] = [];

        if (cacheValue && Array.isArray(cacheValue) && cacheValue.length) {
            cacheValue.forEach((item: { expire: number | undefined; key: string; }) => {
                if (item) {
                    if ((item.expire !== undefined && item.expire > time) || item.expire === 0) {
                        newTagValue.push(item);
                    } else {
                        // 删除过期缓存
                        this.removeItem(item.key);
                    }
                }
            });
        }
        // 保存没有过期的缓存标签
        if (newTagValue.length !== cacheValue.length) {
            this.db.setItem(this.cacheExpire, newTagValue);
        }
    }
}

// 全局缓存
export const Cache = new CacheIntegrated({ db: cache });

// 日志缓存
export const LogCache = new CacheIntegrated({ db: logCache, downgradeDriver: SessionDriver._driver });
